/**
 * Which Fatoraty environment this control panel is running in, and the URLs that go with it.
 *
 * One build serves every environment: opened on a host with "staging" in its name it is staging,
 * on localhost (or under npm start) it is development, anywhere else it is production - so the
 * default is fatoraty.com. REACT_APP_ENV forces the environment, and an explicit REACT_APP_SERVER_URL
 * or REACT_APP_PUBLIC_SITE_URL replaces that one URL (see .env for local development).
 *
 * Read the URLs from here, never from process.env or a hard-coded host.
 */
export type AppEnv = "production" | "staging" | "development";

export interface Environment {
  appEnv: AppEnv;
  /** API base, with a trailing slash */
  serverUrl: string;
  /** Public Fatoraty web app (help center, legal pages), without a trailing slash */
  publicSiteUrl: string;
}

type BuildVariables = Record<string, string | undefined>;

const APP_ENVIRONMENTS: AppEnv[] = ["production", "staging", "development"];

const URL_DEFAULTS: Record<AppEnv, { serverUrl: string; publicSiteUrl: string }> = {
  production: { serverUrl: "https://api.fatoraty.com/api/", publicSiteUrl: "https://fatoraty.com" },
  staging: { serverUrl: "https://api.staging.fatoraty.com/api/", publicSiteUrl: "https://staging.fatoraty.com" },
  // Local control panels have always linked help pages to the staging site
  development: { serverUrl: "http://localhost:3001/api/", publicSiteUrl: "https://staging.fatoraty.com" },
};

const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1"];

const resolveAppEnv = (env: BuildVariables, hostname: string): AppEnv => {
  const explicit = (env.REACT_APP_ENV || "").trim().toLowerCase() as AppEnv;
  if (APP_ENVIRONMENTS.includes(explicit)) return explicit;

  if (env.NODE_ENV !== "production" || LOCAL_HOSTNAMES.includes(hostname)) return "development";
  if (hostname.includes("staging")) return "staging";
  return "production";
};

export const buildEnvironment = ({ env, hostname }: { env: BuildVariables; hostname: string }): Environment => {
  const appEnv = resolveAppEnv(env, hostname);
  const defaults = URL_DEFAULTS[appEnv];

  return {
    appEnv,
    serverUrl: env.REACT_APP_SERVER_URL || defaults.serverUrl,
    publicSiteUrl: env.REACT_APP_PUBLIC_SITE_URL || defaults.publicSiteUrl,
  };
};

const ENVIRONMENT = buildEnvironment({
  env: {
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_ENV: process.env.REACT_APP_ENV,
    REACT_APP_SERVER_URL: process.env.REACT_APP_SERVER_URL,
    REACT_APP_PUBLIC_SITE_URL: process.env.REACT_APP_PUBLIC_SITE_URL,
  },
  hostname: window.location.hostname,
});

export default ENVIRONMENT;
